/**
 * Billing — current plan, available tiers, payment history shell.
 *
 * Tier data, pricing and feature lists are kept in sync with the landing
 * page (apps/landing/src/components/sections/pricing.tsx) — if you change
 * one, change the other.
 *
 * Stripe wiring is deliberately deferred per project memory
 * ([[billing-deferred]]); the page is a faithful shell so the IA is
 * testable without committing to a billing pipeline.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Check, Sparkles, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usersApi } from '@/api/users';
import { PageHeader } from './PageHeader';
import { cn } from '@/lib/utils';

type PlanKey = 'starter' | 'family' | 'family_plus';
type Period = 'monthly' | 'yearly';

interface Price {
  price: string;
  cadence: string;
  sub?: string;
}

interface PlanTier {
  key: PlanKey;
  label: string;
  tagline: string;
  monthly: Price;
  yearly: Price;
  features: string[];
  accent: { ring: string; chip: string; text: string };
  recommended?: boolean;
}

// Mirror of apps/landing/src/components/sections/pricing.tsx PLANS.
const PLANS: PlanTier[] = [
  {
    key: 'starter',
    label: 'Starter',
    tagline: 'Genuinely useful for small families. Safety basics never paywalled.',
    monthly: { price: 'Free', cadence: 'forever' },
    yearly: { price: 'Free', cadence: 'forever' },
    features: [
      '1 child profile',
      'Live location, SOS, panic button',
      'Basic screen-time cap',
      '24-hour location history',
      'Adult-content filter',
      'Scam Shield: scam & phishing alerts',
      'Safe ID emergency tag (1 child)',
    ],
    accent: { ring: 'ring-slate-200', chip: 'bg-slate-100 text-slate-700', text: 'text-slate-700' },
  },
  {
    key: 'family',
    label: 'Family',
    tagline: 'For active parents. Multi-child, history, schedules, reports.',
    monthly: { price: '$6.99', cadence: 'per month' },
    yearly: { price: '$69', cadence: 'per year', sub: '≈ $5.75/mo · save 18%' },
    features: [
      'Up to 4 children',
      '15-second live refresh',
      '30 days of location history',
      'Up to 10 geofences with schedules',
      'Per-app limits + bedtime / school modes',
      'AI Family Safety Assistant + monthly PDF reports',
      'Pickup ETA, Safe Walk & smart risk alerts',
      'Family calendar + Pet Guard (1 pet)',
      'Weekly reports + data export',
    ],
    accent: { ring: 'ring-[#4f6bff]/40', chip: 'bg-[#4f6bff]/12 text-[#4f6bff]', text: 'text-[#4f6bff]' },
    recommended: true,
  },
  {
    key: 'family_plus',
    label: 'Family+',
    tagline: 'Bigger families, teen drivers, advanced safety and reports.',
    monthly: { price: '$12.99', cadence: 'per month' },
    yearly: { price: '$129', cadence: 'per year', sub: '≈ $10.75/mo · save 17%' },
    features: [
      'Up to 8 children',
      '5-second live refresh',
      '12 months of location history',
      'Unlimited geofences (polygons too)',
      'Driving safety reports',
      'Pet Guard: unlimited pets + Lost Pet Mode',
      'Safe ID & Scam Shield for the whole family',
      'Priority chat support · concierge setup',
    ],
    accent: { ring: 'ring-[#ff7a59]/40', chip: 'bg-[#ff7a59]/12 text-[#ff7a59]', text: 'text-[#ff7a59]' },
  },
];

function planMeta(key: string | null | undefined): PlanTier {
  return PLANS.find((p) => p.key === key) ?? PLANS[0]!;
}

export function BillingPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>('monthly');
  const family = useQuery({
    queryKey: ['family-me'],
    queryFn: usersApi.myFamily,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const currentPlanKey = family.data?.plan ?? 'starter';
  const current = planMeta(currentPlanKey);
  const currentPrice = current[period];

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.billing.title')}
        icon={CreditCard}
        tint="amber"
        description={t('pages.billing.description')}
      />

      {/* Current plan hero. */}
      <Card className={cn('mt-6 ring-2', current.accent.ring)}>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                Current plan
              </p>
              <div className="mt-1 flex items-center gap-2">
                <CardTitle className="text-2xl">{current.label}</CardTitle>
                {current.recommended && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                    <Sparkles className="size-3" />
                    Most popular
                  </span>
                )}
              </div>
              <CardDescription className="mt-1">{current.tagline}</CardDescription>
            </div>
            <div className="text-right">
              <p className="text-3xl font-extrabold tracking-tight">
                {family.isPending ? <Skeleton className="h-8 w-20" /> : currentPrice.price}
              </p>
              <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                {currentPrice.cadence}
              </p>
              {currentPrice.sub && (
                <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">
                  {currentPrice.sub}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled className="rounded-full">
              <CreditCard className="size-4" />
              Manage billing (coming soon)
            </Button>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <ShieldCheck className="size-3.5" />
              Safety basics never paywalled
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Plan tiers. */}
      <div className="mt-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Choose the right plan</h2>
            <p className="mt-1 text-sm text-[color:var(--kz-color-ink-secondary)]">
              Every plan keeps the basics — SOS, location, alerts — free for the whole family.
            </p>
          </div>
          {/* Monthly / yearly toggle — matches landing page. */}
          <div
            role="tablist"
            aria-label="Billing period"
            className="inline-flex rounded-full border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-1 text-xs font-semibold"
          >
            <button
              type="button"
              role="tab"
              aria-selected={period === 'monthly'}
              onClick={() => setPeriod('monthly')}
              className={cn(
                'rounded-full px-3 py-1 transition-colors',
                period === 'monthly'
                  ? 'bg-[#4f6bff] text-white shadow-sm'
                  : 'text-[color:var(--kz-color-ink-secondary)] hover:text-[color:var(--kz-color-ink-primary)]',
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={period === 'yearly'}
              onClick={() => setPeriod('yearly')}
              className={cn(
                'rounded-full px-3 py-1 transition-colors',
                period === 'yearly'
                  ? 'bg-[#4f6bff] text-white shadow-sm'
                  : 'text-[color:var(--kz-color-ink-secondary)] hover:text-[color:var(--kz-color-ink-primary)]',
              )}
            >
              Yearly
              <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                Save 18%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {PLANS.map((plan) => {
            const isCurrent = plan.key === currentPlanKey;
            const p = plan[period];
            return (
              <Card
                key={plan.key}
                className={cn(
                  'relative ring-1',
                  isCurrent ? plan.accent.ring : 'ring-[color:var(--kz-color-border-subtle)]/50',
                )}
              >
                {plan.recommended && (
                  <span className="absolute -top-2 right-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#4f6bff] to-[#7a5cff] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow">
                    <Sparkles className="size-3" />
                    Recommended
                  </span>
                )}
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className={cn('text-lg', plan.accent.text)}>{plan.label}</CardTitle>
                    {isCurrent && (
                      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider', plan.accent.chip)}>
                        <Check className="size-3" />
                        Current
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-2xl font-extrabold tracking-tight">
                    {p.price}
                    <span className="ml-1 text-xs font-medium text-[color:var(--kz-color-ink-muted)]">
                      {p.cadence}
                    </span>
                  </p>
                  {p.sub && (
                    <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">
                      {p.sub}
                    </p>
                  )}
                  <CardDescription className="mt-1.5">{plan.tagline}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5 text-sm">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className={cn('mt-0.5 size-4 shrink-0', plan.accent.text)} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isCurrent ? 'outline' : 'default'}
                    disabled
                    className="mt-4 w-full rounded-full"
                  >
                    {isCurrent ? 'You’re on this plan' : `Switch to ${plan.label}`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payment history placeholder. */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Payment history</CardTitle>
          <CardDescription>Past invoices and receipts.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-4 text-sm text-[color:var(--kz-color-ink-muted)]">
            No invoices yet. Once billing is enabled, your monthly receipts will appear here and be downloadable as PDF.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
